<script setup lang="ts">
import { Clock3, Pause, Play, Square } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { elapsedTimeSeconds, formatTrackedDuration } from '~/lib/timeTracking';
import type { TimeEntry } from '~/types/models';

const props = withDefaults(defineProps<{
    entry?: TimeEntry | null;
    loading?: boolean;
    showLabel?: boolean;
    allowInstantStart?: boolean;
    longPressDelay?: number;
    startLabel?: string;
    stopLabel?: string;
}>(), {
    entry: null,
    loading: false,
    showLabel: false,
    allowInstantStart: false,
    longPressDelay: 550,
    startLabel: 'Start timer',
    stopLabel: 'Stop timer',
});

const emit = defineEmits<{
    start: [];
    'instant-start': [];
    pause: [entryId: string];
    resume: [entryId: string];
    stop: [entryId: string];
}>();

const now = ref(Date.now());
let interval: ReturnType<typeof setInterval> | null = null;
let pressTimer: ReturnType<typeof setTimeout> | null = null;
let suppressResetTimer: ReturnType<typeof setTimeout> | null = null;
const longPressTriggered = ref(false);
const suppressPointerClick = ref(false);

const elapsedLabel = computed(() => props.entry
    ? formatTrackedDuration(elapsedTimeSeconds(props.entry, now.value))
    : '');

onMounted(() => {
    interval = setInterval(() => { now.value = Date.now(); }, 1000);
});

onBeforeUnmount(() => {
    if (interval) clearInterval(interval);
    if (pressTimer) clearTimeout(pressTimer);
    if (suppressResetTimer) clearTimeout(suppressResetTimer);
    removeGlobalPointerEndListeners();
});

const tooltipLabel = computed(() => props.entry
    ? `${props.entry.status === 'paused' ? 'Resume' : 'Pause'} timer: ${props.entry.taskTitle || props.entry.projectName || props.entry.description || 'workspace time'}`
    : props.allowInstantStart
        ? `${props.startLabel}. Hold to start instantly.`
        : props.startLabel);

function clearPressTimer() {
    if (!pressTimer) return;
    clearTimeout(pressTimer);
    pressTimer = null;
}

function handlePointerDown(event: PointerEvent) {
    if (props.entry || props.loading || !props.allowInstantStart || (event.pointerType === 'mouse' && event.button !== 0)) return;
    longPressTriggered.value = false;
    suppressPointerClick.value = false;
    clearPressTimer();
    if (import.meta.client) {
        removeGlobalPointerEndListeners();
        window.addEventListener('pointerup', handleGlobalPointerEnd, true);
        window.addEventListener('pointercancel', handleGlobalPointerEnd, true);
    }
    pressTimer = setTimeout(() => {
        longPressTriggered.value = true;
        suppressPointerClick.value = true;
        emit('instant-start');
    }, props.longPressDelay);
}

function removeGlobalPointerEndListeners() {
    if (!import.meta.client) return;
    window.removeEventListener('pointerup', handleGlobalPointerEnd, true);
    window.removeEventListener('pointercancel', handleGlobalPointerEnd, true);
}

function handleGlobalPointerEnd() {
    removeGlobalPointerEndListeners();
    clearPressTimer();
    if (!longPressTriggered.value) return;
    suppressPointerClick.value = true;
    if (suppressResetTimer) clearTimeout(suppressResetTimer);
    suppressResetTimer = setTimeout(() => {
        longPressTriggered.value = false;
        suppressPointerClick.value = false;
    }, 750);
}

function handleClick() {
    clearPressTimer();
    if (longPressTriggered.value || suppressPointerClick.value) {
        return;
    }
    if (props.entry) {
        emit(props.entry.status === 'paused' ? 'resume' : 'pause', props.entry.id);
        return;
    }
    emit('start');
}

function stopActiveTimer() {
    if (props.entry) emit('stop', props.entry.id);
}
</script>

<template>
    <TooltipProvider :delay-duration="350">
        <Tooltip>
            <TooltipTrigger as-child>
                <Button
                    v-if="!entry"
                    variant="outline"
                    :size="showLabel ? 'sm' : 'icon'"
                    :class="[
                        'gap-2 tabular-nums',
                        !showLabel ? 'size-9' : '',
                    ]"
                    :disabled="loading"
                    :aria-label="tooltipLabel"
                    @pointerdown="handlePointerDown"
                    @pointerup="clearPressTimer"
                    @pointercancel="clearPressTimer"
                    @pointerleave="clearPressTimer"
                    @contextmenu.prevent
                    @click="handleClick"
                >
                    <Clock3 v-if="loading" class="size-3.5 animate-pulse" />
                    <Play v-else class="size-3.5 fill-current" />
                    <span v-if="showLabel">{{ startLabel }}</span>
                </Button>
                <div v-else class="inline-flex h-9 items-center overflow-hidden rounded-md border border-primary/35 bg-card text-primary shadow-sm">
                    <Button
                        variant="ghost"
                        size="sm"
                        class="h-full min-w-[6.75rem] gap-2 rounded-none bg-card px-3 text-primary hover:bg-primary/10 hover:text-primary"
                        :disabled="loading"
                        :aria-label="tooltipLabel"
                        @click="handleClick"
                    >
                        <Play v-if="entry.status === 'paused'" class="size-3.5 fill-current" />
                        <Pause v-else class="size-3.5 fill-current" />
                        <span class="font-mono text-xs font-semibold">{{ elapsedLabel }}</span>
                    </Button>
                    <span class="h-5 w-px bg-primary/20" aria-hidden="true" />
                    <Button
                        variant="ghost"
                        size="icon"
                        class="h-full w-9 rounded-none bg-card text-primary hover:bg-primary/10 hover:text-primary"
                        :disabled="loading"
                        :aria-label="stopLabel"
                        @click="stopActiveTimer"
                    >
                        <Square class="size-3 fill-current" />
                    </Button>
                </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" class="max-w-56 text-center">
                {{ tooltipLabel }}
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>
